import { Component, ElementRef, Input, Output, EventEmitter, ContentChild, ContentChildren, QueryList, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, ViewChild } from '@angular/core';
import type { AfterContentInit, OnInit, OnChanges, OnDestroy,SimpleChanges, TrackByFunction } from '@angular/core';
import { ObjectUtils } from 'primeng/utils';
import { Header, Footer, PrimeTemplate, FilterService, TranslationKeys, PrimeNGConfig } from 'primeng/api';
import type { BlockableUI } from 'primeng/api';
import { Subscription } from 'rxjs';
import { NodeEntityModels } from '@medsurf/flat-models';
import { DragContainerComponent } from '@medsurf/flat-components';
import { DragulaService } from 'ng2-dragula';
@Component({
    selector: 'medsurf-design-content-sortable-data-view',
    templateUrl: './sortable-data-view.content.design.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./sortable-data-view.content.design.scss'],
    host: {
        class: 'p-element'
    },
    standalone: false
})
export class SortableDataViewContentDesign extends DragContainerComponent implements OnInit, AfterContentInit, OnDestroy, BlockableUI, OnChanges {

  listItemTemplate: TemplateRef<any>;
  gridItemTemplate: TemplateRef<any>;
  itemTemplate: TemplateRef<any>;
  headerTemplate: TemplateRef<any>;
  emptyMessageTemplate: TemplateRef<any>;
  footerTemplate: TemplateRef<any>;
  paginatorLeftTemplate: TemplateRef<any>;
  paginatorRightTemplate: TemplateRef<any>;
  paginatorDropdownItemTemplate: TemplateRef<any>;
  filteredValue: any[] | null;
  filterValue: string;
  initialized: boolean;
  translationSubscription: Subscription;

  @Input() paginator: boolean;
  @Input() rows: number;
  @Input() totalRecords: number;
  @Input() pageLinks = 5;
  @Input() rowsPerPageOptions: any[];
  @Input() paginatorPosition = 'bottom';
  @Input() alwaysShowPaginator = true;
  @Input() paginatorDropdownAppendTo: any;
  @Input() paginatorDropdownScrollHeight = '200px';
  @Input() currentPageReportTemplate = '{currentPage} of {totalPages}';
  @Input() showCurrentPageReport: boolean;
  @Input() showJumpToPageDropdown: boolean;
  @Input() showFirstLastIcon = true;
  @Input() showPageLinks = true;
  @Input() lazy: boolean;
  @Input() emptyMessage = '';
  @Input() style: any;
  @Input() styleClass = 'flex flex-1 flex-column w-full h-full';
  @Input() gridStyleClass = '';
  @Input() listStyleClass = '';
  @Input() draggable = true;
  @Input() trackBy: TrackByFunction<any> = (index: number, item: any) => item;
  @Input() filterBy: string;
  @Input() filterLocale: string;
  @Input() loading: boolean;
  @Input() first = 0;
  @Input() sortField: string;
  @Input() sortOrder: number;
  @Input() parent!: NodeEntityModels.NodeEntityType;

  @Output() onLazyLoad: EventEmitter<any> = new EventEmitter();
  @Output() onPage: EventEmitter<any> = new EventEmitter();
  @Output() onSort: EventEmitter<any> = new EventEmitter();
  @Output() onChangeLayout: EventEmitter<any> = new EventEmitter();
  @Output() onNodeMoved: EventEmitter<{ 
    parent: NodeEntityModels.NodeEntityType,
    children: NodeEntityModels.NodeEntityType[]
  }> = new EventEmitter();

  @ContentChild(Header) header: any;
  @ContentChild(Footer) footer: any;
  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  @ViewChild('sortable_data_view_scroll_panel', {static: false}) sortableDataViewScrollPanel: ElementRef<HTMLDivElement>;

  #_layout: 'list' | 'grid' = 'list';
  @Input() 
  set layout(layout: 'list' | 'grid') {
    this.#_layout = layout;
    if (this.initialized) {
      this.changeLayout();
    }
  }
  get layout() { return this.#_layout; }

  #_value: any[];
  @Input() 
  set value(value: any[]) {
    this.#_value = value;
    this.updateTotalRecords();
    if (!this.lazy && this.hasFilter()) {
      this.filter(this.filterValue);
    }
  }
  get value() { return this.#_value; }

  constructor(private el: ElementRef,
              private cd: ChangeDetectorRef,
              private filterService: FilterService,
              private config: PrimeNGConfig,
              dragulaService: DragulaService) {
    super(dragulaService);
  }

  ngOnInit() {
    if (this.lazy) {
      this.onLazyLoad.emit(this.createLazyLoadMetadata());
    }

    this.translationSubscription = this.config.translationObserver.subscribe(() => {
      this.cd.markForCheck();
    });
    this.initialized = true;
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges['sortField'] || simpleChanges['sortOrder']) {
      //avoid triggering lazy load prior to lazy initialization at onInit
      if (!this.lazy || this.initialized) {
        this.sort();
      }
    }
    if (simpleChanges['first']) {
      this.sortableDataViewScrollPanel?.nativeElement?.scrollTo({ top: 0 });
    }
  }

  ngAfterContentInit() {
    for (const item of this.templates) {
      switch (item.getType()) {
        case 'listItem':
          this.listItemTemplate = item.template;
          break;
        case 'gridItem':
          this.gridItemTemplate = item.template;
          break;
        case 'paginatorleft':
          this.paginatorLeftTemplate = item.template;
          break;
        case 'paginatorright':
          this.paginatorRightTemplate = item.template;
          break;
        case 'paginatordropdownitem':
          this.paginatorDropdownItemTemplate = item.template;
          break;
        case 'empty':
          this.emptyMessageTemplate = item.template;
          break;
        case 'header':
          this.headerTemplate = item.template;
          break;
        case 'footer':
          this.footerTemplate = item.template;
          break;
      }
    }
    this.updateItemTemplate();
  }

  updateItemTemplate() {
    switch (this.layout) {
      case 'list':
        this.itemTemplate = this.listItemTemplate;
        break;
      case 'grid':
        this.itemTemplate = this.gridItemTemplate;
        break;
    }
  }

  changeLayout() {
    this.onChangeLayout.emit({
      layout: this.layout
    });
    this.updateItemTemplate();

    this.cd.markForCheck();
  }

  updateTotalRecords() {
    this.totalRecords = this.lazy ? this.totalRecords : this.value?.length ?? 0;
  }

  paginate(event: any) {
    this.first = event.first;
    this.rows = event.rows;

    if (this.lazy) {
      this.onLazyLoad.emit(this.createLazyLoadMetadata());
    }

    this.onPage.emit({
      first: this.first,
      rows: this.rows
    });
  }

  sort() {
    this.first = 0;

    if (this.lazy) {
      this.onLazyLoad.emit(this.createLazyLoadMetadata());
    } else if (this.value) {
      this.value.sort((data1: any, data2: any) => {
        const value1 = ObjectUtils.resolveFieldData(data1, this.sortField);
        const value2 = ObjectUtils.resolveFieldData(data2, this.sortField);
        
        let result;
        if (value1 == null && value2 != null) result = -1;
        else if (value1 != null && value2 == null) result = 1;
        else if (value1 == null && value2 == null) result = 0;
        else if (typeof value1 === 'string' && typeof value2 === 'string') result = value1.localeCompare(value2);
        else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;

        return this.sortOrder * result;
      });

      if (this.hasFilter()) {
        this.filter(this.filterValue);
      }
    }

    this.onSort.emit({
      sortField: this.sortField,
      sortOrder: this.sortOrder
    });
  }

  get IsEmpty() {
    const data = this.filteredValue || this.value;
    return data == null || data.length == 0;
  }

  createLazyLoadMetadata(): any {
    return {
      first: this.first,
      rows: this.rows,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    };
  }

  getBlockableElement(): HTMLElement {
    return this.el.nativeElement.children[0];
  }

  get emptyMessageLabel(): string {
    return this.emptyMessage || this.config.getTranslation(TranslationKeys.EMPTY_MESSAGE);
  }

  filter(filter: string, filterMatchMode = 'contains') {
    this.filterValue = filter;
    if (this.value?.length) {
      const searchFields = this.filterBy.split(',');
      this.filteredValue = this.filterService.filter(this.value, searchFields, filter, filterMatchMode, this.filterLocale);

      if (this.filteredValue.length === this.value.length) {
        this.filteredValue = null;
      }

      if (this.paginator) {
        this.first = 0;
        this.totalRecords = this.filteredValue ? this.filteredValue.length : this.value ? this.value.length : 0;
      }
      this.cd.markForCheck();
    }
  }

  hasFilter() {
    return this.filterValue && this.filterValue.trim().length > 0;
  }

  scrollTo(scrollTop: number) {
    if (this.sortableDataViewScrollPanel) {
      this.sortableDataViewScrollPanel.nativeElement.scrollTop = scrollTop;
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }
}

@Component({
    selector: 'medsurf-design-content-sortable-data-view-layout-options',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div [ngClass]="'p-dataview-layout-options p-selectbutton p-buttonset'" [ngStyle]="style" [class]="styleClass">
            <button type="button" class="p-button p-button-icon-only" [ngClass]="{ 'p-highlight': dv.layout === 'list' }" (click)="changeLayout($event, 'list')" (keydown.enter)="changeLayout($event, 'list')">
                <i class="pi pi-bars"></i></button
            ><button type="button" class="p-button p-button-icon-only" [ngClass]="{ 'p-highlight': dv.layout === 'grid' }" (click)="changeLayout($event, 'grid')" (keydown.enter)="changeLayout($event, 'grid')">
                <i class="pi pi-th-large"></i>
            </button>
        </div>
    `,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'p-element'
    },
    standalone: false
})
export class SortableDataViewLayoutOptionsContentDesign {
  @Input() style: any;
  @Input() styleClass: string;
  @Input() layout: 'list' | 'grid';

  constructor(public dv: SortableDataViewContentDesign) {
  }

  changeLayout(event: Event,
                      layout: 'list' | 'grid') {
    this.dv.layout = layout;
    event.preventDefault();
  }
}
